import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { FaTimes } from "react-icons/fa";

const ReviewQuiz = () => {
  const [searchParams] = useSearchParams();
  const challengeId = searchParams.get("challengeId");
  const [reviewData, setReviewData] = useState(null);
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const baseUrl = import.meta.env.VITE_APP_BASE_URL;
  const userId = localStorage.getItem("userId");

  useEffect(() => {
    const fetchReviewData = async () => {
      console.log("Fetching review data for challengeId:", challengeId);
      if (!challengeId) {
        console.error("No challengeId found in URL");
        setError("Challenge ID not found.");
        setStatus("error");
        return;
      }
      try {
        const url = `${baseUrl}/play/challenges/${challengeId}/review?user_id=${userId}`;
        console.log("Making API request to:", url);
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        const data = await response.json();
        console.log("Received review data:", data);

        // Log questions data to inspect structure
        console.log("Questions data from API:", data?.questions);

        setReviewData(data);
        setStatus("complete");
      } catch (error) {
        console.error("Error fetching review data:", error);
        setError(error.message);
        setStatus("error");
      }
    };
    fetchReviewData();
  }, [challengeId, baseUrl, userId]);

  const handleNeedHelp = (question) => {
    console.log("Attempting to navigate to tutor with question:", question);
    let questionWithOptions = "";
    if (question && question.question) {
      questionWithOptions = `${question.question}\n\nOptions:\n`;
      // Ensure options exist and are an array before mapping
      if (question.options && Array.isArray(question.options)) {
        question.options.forEach((option, index) => {
          questionWithOptions += `${String.fromCharCode(65 + index)}. ${option}\n`;
        });
      }
      questionWithOptions += "\nPlease help me understand this step by step.";
    } else {
      // Fallback if question or question_text is missing
      questionWithOptions =
        "Could not load question details. Please help me understand this step by step.";
    }

    console.log("Prefilled text being sent:", questionWithOptions);
    console.log("Navigation state being sent:", {
      prefilledText: questionWithOptions,
      activeMode: "stepbystep",
      challengeId: challengeId,
      questionId: question ? question.id : null,
      subject: reviewData ? reviewData.subject : null,
      topic: reviewData ? reviewData.topic : null,
      level: reviewData ? reviewData.level : null,
    });

    navigate("/tutor", {
      state: {
        prefilledText: questionWithOptions,
        activeMode: "stepbystep",
        challengeId: challengeId,
        questionId: question ? question.id : null,
        subject: reviewData ? reviewData.subject : null,
        topic: reviewData ? reviewData.topic : null,
        level: reviewData ? reviewData.level : null,
      },
    });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (status === "loading") {
    return (
      <div className="flex justify-center items-center h-screen">
        Loading review...
      </div>
    );
  }

  if (status === "error") {
    return (
      <div className="flex flex-col justify-center items-center h-screen text-red-500">
        <p>Error: {error}</p>
        <button
          onClick={() => navigate(-1)}
          className="mt-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2 rounded-lg shadow transition"
        >
          Go Back
        </button>
      </div>
    );
  }

  if (status === "complete" && reviewData) {
    // Add a check for reviewData.questions here
    const questions = reviewData.questions;

    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-2xl shadow-lg p-6 mb-8">
            <h1 className="text-3xl font-bold text-gray-800 mb-4">
              Quiz Review
            </h1>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-sm text-gray-500">Subject</p>
                <p className="font-semibold text-gray-800">
                  {reviewData.subject}
                </p>
              </div>
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-sm text-gray-500">Topic</p>
                <p className="font-semibold text-gray-800">
                  {reviewData.topic}
                </p>
              </div>
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-sm text-gray-500">Level</p>
                <p className="font-semibold text-gray-800">
                  {reviewData.level}
                </p>
              </div>
              <div className="bg-gray-50 rounded-xl p-4">
                <p className="text-sm text-gray-500">Your Score</p>
                <p className="font-semibold text-gray-800">
                  {reviewData.user_score}/{reviewData.total_questions}
                </p>
              </div>
            </div>
          </div>

          {/* Questions */}
          <div className="space-y-6">
            {/* Add a check here to ensure questions is an array before mapping */}
            {questions &&
              Array.isArray(questions) &&
              questions.map((question, index) => (
                <div
                  key={question.id}
                  className="bg-white rounded-2xl shadow-lg p-6"
                >
                  <div className="flex items-start gap-4">
                    <div className="bg-blue-100 text-blue-600 font-bold rounded-full w-8 h-8 flex items-center justify-center flex-shrink-0">
                      {index + 1}
                    </div>
                    <div className="flex-grow">
                      <div className="flex justify-between items-start mb-4">
                        <h3 className="text-lg font-semibold text-gray-800">
                          {question.question}
                        </h3>
                        {/* Removed Ask Annya Button */}
                        <button
                          onClick={() => handleNeedHelp(question)}
                          className="flex items-center gap-2 text-purple-600 hover:text-purple-700 font-medium ml-4"
                        >
                          Need Help?
                        </button>
                      </div>

                      {/* Options */}
                      <div className="space-y-3">
                        {/* Add a check here to ensure options is an array before mapping */}
                        {question.options &&
                          Array.isArray(question.options) &&
                          question.options.map((option, optIndex) => (
                            <div
                              key={optIndex}
                              className={`p-3 rounded-lg border-2 ${
                                option === question.correct_answer
                                  ? "border-green-500 bg-green-50"
                                  : option === question.user_answer &&
                                      option !== question.correct_answer
                                    ? "border-red-500 bg-red-50"
                                    : "border-gray-200"
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                <span className="font-medium text-gray-600">
                                  {String.fromCharCode(65 + optIndex)}.
                                </span>
                                <span className="text-gray-800">{option}</span>
                                {option === question.correct_answer && (
                                  <span className="ml-auto text-green-600">
                                    ✓ Correct Answer
                                  </span>
                                )}
                                {option === question.user_answer &&
                                  option !== question.correct_answer && (
                                    <span className="ml-auto text-red-600">
                                      ✗ Your Answer
                                    </span>
                                  )}
                              </div>
                            </div>
                          ))}
                      </div>

                      {/* Explanation */}
                      {question.explanation && (
                        <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                          <h4 className="font-semibold text-blue-800 mb-2">
                            Explanation:
                          </h4>
                          <p className="text-blue-700">
                            {question.explanation}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}

            {/* Add message if no questions are available or questions is not an array */}
            {(!questions ||
              !Array.isArray(questions) ||
              questions.length === 0) && (
              <div className="text-center text-gray-600 text-xl">
                No questions available for review.
              </div>
            )}
          </div>

          {/* Back Button */}
          <div className="mt-8 flex justify-center">
            <button
              onClick={() => navigate(-1)}
              className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-3 rounded-lg shadow transition"
            >
              Back to Results
            </button>
          </div>
        </div>

        {/* Removed Ask Annya Modal */}
      </div>
    );
  }
  return null;
};

export default ReviewQuiz;
